import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { CompanyStatus, ResponseCompany } from '@/schema'
import { SortOrder } from '@/types/common'
import { DEFAULT_PAGE } from '@/utils/constants'

import { CompanyFilters } from './types'

const DEVTOOLS_NAME = 'CompanyStore'

// Company store state and actions
interface CompanyStore {
  // State
  searchText: string
  filters: CompanyFilters
  page: number
  sort: {
    column: keyof ResponseCompany
    direction: SortOrder
  }
  editingCompany: ResponseCompany | null
  dialogOpen: boolean
  deletingCompany: ResponseCompany | null
  deleteDialogOpen: boolean

  // Actions
  setSearchText: (searchText: string) => void
  setFilter: <K extends keyof CompanyFilters>(
    key: K,
    value: CompanyFilters[K]
  ) => void
  setPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
  setSort: (sort: {
    column: keyof ResponseCompany | null
    direction: SortOrder
  }) => void
  setEditingCompany: (company: ResponseCompany | null) => void
  clearEditingCompany: () => void
  setDialogOpen: (open: boolean) => void
  setDeletingCompany: (company: ResponseCompany | null) => void
  clearDeletingCompany: () => void
  setDeleteDialogOpen: (open: boolean) => void
  resetFilters: () => void
  reset: () => void
}

// Initial state
const initialState = {
  searchText: '',
  filters: {
    searchName: '',
    status: CompanyStatus.ACTIVE,
  } as CompanyFilters,
  page: DEFAULT_PAGE,
  sort: {
    column: 'created_at' as keyof ResponseCompany,
    direction: 'desc' as SortOrder,
  },
  editingCompany: null as ResponseCompany | null,
  dialogOpen: false,
  deletingCompany: null as ResponseCompany | null,
  deleteDialogOpen: false,
}

/**
 * Company Store
 *
 * Manages filter, search, pagination, and sorting state for companies.
 *
 * Features:
 * - Search by name
 * - Pagination state
 * - Sorting state
 * - Edit/Delete state management
 * - Reset functionality
 * - Redux DevTools integration
 *
 * @example
 * ```tsx
 * // In a component
 * const { filters, setFilter, setPage } = useCompanyStore()
 *
 * // Set search
 * setFilter('searchName', 'Acme Corp')
 *
 * // Change page
 * setPage(2)
 *
 * // Set editing company
 * setEditingCompany(company)
 * ```
 */
export const useCompanyStore = create<CompanyStore>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Set search text
       */
      setSearchText: (searchText: string) => {
        set({ searchText }, false, 'setSearchText')
      },

      /**
       * Set a filter value
       */
      setFilter: <K extends keyof CompanyFilters>(
        key: K,
        value: CompanyFilters[K]
      ) => {
        set(
          (state) => ({
            filters: { ...state.filters, [key]: value },
            page: DEFAULT_PAGE,
          }),
          false,
          `setFilter/${key}`
        )
      },

      /**
       * Set current page
       */
      setPage: (page: number) => {
        set({ page }, false, 'setPage')
      },

      /**
       * Set sort column and direction
       */
      setSort: (sort: {
        column: keyof ResponseCompany
        direction: SortOrder
      }) => {
        set({ sort }, false, 'setSort')
      },

      /**
       * Set the company being edited
       */
      setEditingCompany: (company: ResponseCompany | null) => {
        set(
          { editingCompany: company, dialogOpen: true },
          false,
          'setEditingCompany'
        )
      },

      /**
       * Clear the editing company
       */
      clearEditingCompany: () => {
        set(
          { editingCompany: null, dialogOpen: false },
          false,
          'clearEditingCompany'
        )
      },

      /**
       * Set dialog open/close state
       */
      setDialogOpen: (open: boolean) => {
        set({ dialogOpen: open }, false, 'setDialogOpen')
      },

      /**
       * Set the company being deleted
       */
      setDeletingCompany: (company: ResponseCompany | null) => {
        set(
          { deletingCompany: company, deleteDialogOpen: true },
          false,
          'setDeletingCompany'
        )
      },

      /**
       * Clear the deleting company
       */
      clearDeletingCompany: () => {
        set(
          { deletingCompany: null, deleteDialogOpen: false },
          false,
          'clearDeletingCompany'
        )
      },

      /**
       * Set delete dialog open/close state
       */
      setDeleteDialogOpen: (open: boolean) => {
        set({ deleteDialogOpen: open }, false, 'setDeleteDialogOpen')
      },

      /**
       * Reset all filters to initial state
       */
      resetFilters: () => {
        set({ filters: initialState.filters }, false, 'resetFilters')
      },

      /**
       * Reset entire store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    { name: DEVTOOLS_NAME }
  )
)
